import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram, Facebook } from "lucide-react";
import { FooterInstallButton } from "./PwaInstall";

// TikTok glyph (lucide-react has no TikTok icon yet)
const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.18a8.16 8.16 0 0 0 4.77 1.52V6.21a4.79 4.79 0 0 1-1.84-.12z" />
  </svg>
);

const SOCIAL_LINKS = [
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@rivones0",
    Icon: TikTokIcon,
    testid: "social-tiktok",
    // TikTok's on-brand look: black glyph on white, cyan/pink halo on hover.
    // We use a solid black chip so the icon reads instantly at 36×36.
    chipClass: "bg-black text-white hover:bg-neutral-800",
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/rivones_app",
    Icon: Instagram,
    testid: "social-instagram",
    // Instagram's iconic yellow→pink→purple gradient (per their 2016+
    // brand kit). Rendered as a linear-gradient chip so the whole thing
    // reads as "Instagram" even without the wordmark.
    chipClass:
      "text-white hover:brightness-110 bg-gradient-to-tr from-[#FEDA75] via-[#FA7E1E] via-30% to-[#D62976] to-70%",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/share/1AyD2fk7bM/",
    Icon: Facebook,
    testid: "social-facebook",
    // Facebook Blue (#1877F2) is the canonical brand color per Meta's
    // brand guidelines.
    chipClass: "bg-[#1877F2] text-white hover:bg-[#0e5fd6]",
  },
];

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const links = {
    explore: [
      { label: t("footer.searchCars"), href: "/search" },
      { label: t("footer.howItWorks"), href: "/como-funciona" },
      { label: t("footer.protectionPlans"), href: "/planes-de-proteccion" },
      { label: t("footer.faq"), href: "/preguntas-frecuentes" },
    ],
    host: [
      { label: t("footer.listYourCar"), href: "/host/dashboard" },
      { label: t("footer.resourceCenter"), href: "/centro-de-recursos" },
      { label: t("footer.earningsCalculator"), href: "/calculadora-de-ganancias" },
      { label: t("footer.hostRequirements"), href: "/requisitos-host" },
    ],
    company: [
      { label: t("footer.careers"), href: "/carreras" },
      { label: t("footer.press"), href: "/prensa" },
      { label: t("footer.contact"), href: "/contacto" },
    ],
    legal: [
      { label: t("footer.about"), href: "/sobre-nosotros" },
      { label: t("footer.terms"), href: "/terminos" },
      { label: t("footer.eligibility"), href: "/elegibilidad" },
      { label: t("footer.privacy"), href: "/privacidad" },
      { label: t("footer.deleteAccount"), href: "/eliminar-cuenta" },
      { label: t("footer.cancellation"), href: "/cancelacion" },
    ],
  };

  return (
    <footer className="bg-[#FAFAFA] border-t border-neutral-200 mt-auto" data-testid="site-footer">
      <div className="container-main py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src="/assets/rivones-logo.jpg" alt="Rivones" className="w-9 h-9 rounded-lg object-cover" />
              <span className="font-heading font-bold text-lg">Rivones</span>
            </Link>
            <p className="text-sm text-neutral-500 leading-relaxed mb-6">{t("footer.tagline")}</p>
            <div className="flex gap-3">
              {SOCIAL_LINKS.map(({ name, href, Icon, testid, chipClass }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  data-testid={testid}
                  className={`w-9 h-9 rounded-full flex items-center justify-center shadow-sm transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-400 ${chipClass}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            {/* PWA install CTA — renders the right affordance per device:
                Chrome/Edge → native prompt, iOS Safari → visual guide
                modal, already installed → subtle "instalada" chip. */}
            <FooterInstallButton />
          </div>

          {/* Link columns */}
          {[
            { heading: t("footer.explore"), items: links.explore },
            { heading: t("footer.hosts"), items: links.host },
            { heading: t("footer.company"), items: links.company },
            { heading: t("footer.legal"), items: links.legal },
          ].map((col) => (
            <div key={col.heading}>
              <h4 className="font-heading font-semibold text-sm mb-4">{col.heading}</h4>
              <ul className="space-y-3">
                {col.items.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} className="text-sm text-neutral-500 hover:text-[#111111] transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="border-t border-neutral-200 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <p className="text-sm text-neutral-500">{t("footer.copyright", { year })}</p>
            <p className="text-xs text-neutral-400 mt-1">{t("footer.operatedBy")}</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-400">{t("footer.location")}</span>
            <span className="text-neutral-300">•</span>
            <span className="text-sm text-neutral-400">MXN</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
